import { useState } from 'react'
import { useNavigate } from 'react-router'
import logo from '@/assets/logo-wordmark-430.png'
import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import { supabase } from '@/lib/supabase'

export default function SignInPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [phase, setPhase] = useState<'email' | 'sent'>('email')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cleanEmail = () => email.trim().toLowerCase()

  async function sendCode() {
    setBusy(true)
    setError(null)
    const { error } = await supabase.auth.signInWithOtp({
      email: cleanEmail(),
      options: {
        // THE invite-only enforcement. Without it Supabase provisions a user for
        // any address that asks, and "disable public signup" in the dashboard
        // surfaces only as a vague 4xx with no useful message.
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    setBusy(false)
    if (error) {
      // Naming the reason is a deliberate choice. Enumeration is not a threat
      // for a closed crew, and a silent "check your email" for an address that
      // will never receive one is a support text to Brian.
      setError("That address isn't on the list. Text Brian to get added.")
      return
    }
    setPhase('sent')
  }

  async function verifyCode() {
    setBusy(true)
    setError(null)
    const { error } = await supabase.auth.verifyOtp({
      email: cleanEmail(),
      token: code.trim(),
      type: 'email',
    })
    setBusy(false)
    if (error) {
      setError('That code is wrong or has expired. Send a new one.')
      return
    }
    const next = sessionStorage.getItem('sa:redirect') ?? '/'
    sessionStorage.removeItem('sa:redirect')
    navigate(next, { replace: true })
  }

  return (
    <>
      {/* Full-bleed stadium backdrop.
          Decorative only, so aria-hidden and empty alt -- it carries no
          information the form does not already state.

          The asset is pre-cropped (1440x603, 146 KB): the source photo's bottom
          quarter is a wall of silhouetted heads in the near foreground, which
          reads as noise at any size and competes with the form. Cropping it in
          the file rather than with object-position means the bytes are not
          shipped either.

          scale-105 exists because `filter: blur()` samples beyond the element
          and leaves a soft transparent margin at the edges; oversizing pushes
          that margin outside the viewport. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <img
          src="/Side_Action_Picks.webp"
          alt=""
          className="h-full w-full scale-105 object-cover"
          style={{
            // Bias down the frame so the field holds the centre on short, wide
            // viewports, where `cover` crops vertically and would otherwise
            // favour the sky.
            objectPosition: '50% 62%',
            // Desaturation pulls the crowd's orange down so it stops competing
            // with --color-mark green; the blur keeps crowd texture from
            // reading as detail behind the card.
            filter: 'saturate(0.72) blur(3px)',
          }}
        />
        {/* Heavier in the centre, where the card sits. The radial does that
            work; the linear underneath keeps the top and bottom edges from
            going pale where the gradient falls off.

            These weights are tuned for the 380px case, not the desktop one. At
            380px `cover` shows only about a fifth of this photo's width, so the
            backdrop is a narrow vertical slice with far less to read than the
            full sweep a desktop viewport gets. An earlier, heavier pass
            (0.86 centre / 0.46 edge) still looked deliberate at 1440px but
            collapsed into an unreadable murk at 380px.

            Lightening this costs nothing in legibility: the card is 96% opaque,
            so the composite behind the type is dominated by stock and barely
            moves -- measured identical at 4.79:1 before and after. Contrast is
            bought by the card's alpha, not by this gradient. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 85% 65% at 50% 52%, rgba(7,15,26,0.62) 0%, rgba(7,15,26,0.50) 42%, rgba(7,15,26,0.38) 72%, rgba(7,15,26,0.30) 100%),' +
              'linear-gradient(to bottom, rgba(7,15,26,0.24) 0%, rgba(7,15,26,0.08) 38%, rgba(7,15,26,0.28) 100%)',
          }}
        />
      </div>

      <main className="relative z-10 mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6 py-16">
        {/* The card is what keeps the existing type treatment intact. Every
            colour on this page is dark-on-light (--color-ink navy, --color-mark
            green, --color-ink-dim grey on --color-stock); laying that directly
            over a darkened photo would bury it. The card restores the stock
            ground the palette was designed against, and the gradient behind it
            is what makes it sit forward.

            96% rather than opaque so the backdrop still registers through it.
            The number is load-bearing, not taste: contrast here is measured
            against the COMPOSITE, and the eyebrow and EMAIL label are the
            smallest type on the page at 11px, so they set the floor. Worst case
            (a pure black photo pixel under the card) the composite is #e8e9e4
            and they land at 4.79:1. At 0.92 the same measurement gives 4.40:1,
            which fails AA. Do not lower this to show more of the photo. */}
        <div className="bg-stock/96 rounded-row px-6 py-8 shadow-[0_2px_40px_rgba(7,15,26,0.55)] backdrop-blur-[2px]">
          <header className="mb-10">
            <p className="eyebrow mb-4">Invitation only</p>
            {/* The mark carries the words -- SIDE ACTION over a PICKS banner --
                so it REPLACES the set-type wordmark rather than sitting above
                it. Still an h1, and alt carries the brand, so the page keeps
                exactly one heading and an accessible name.

                88px tall restores the vertical weight of the two-line 44px type
                block this replaced (~84px), and fills half the card's 284px
                content column at 380px.

                logo-wordmark-430.png is cut for exactly this: 430x264 is 3x of
                88px, re-derived from logo-transparent.png (1024x1024 master,
                artwork at x45..977 y219..791 = 933x573, aspect 1.6283). 3x
                rather than 2x because most phones are 3x -- a 2x cut would be
                176px tall and land at the same 67% shortfall that made the old
                215px asset soft here in the first place. The extra fidelity
                costs 8.8 KB over that asset (44.1 vs 35.3), which is a bad
                trade to refuse.

                If this ever renders taller than 88px, re-cut from the master
                again. Do not scale this copy up. */}
            <h1>
              <img
                src={logo}
                alt="Side Action Picks"
                width={430}
                height={264}
                className="h-22 w-auto"
              />
            </h1>
            <p className="text-ink-dim text-body mt-4">Sixteen games a week. One lock. No mercy.</p>
          </header>

          {phase === 'email' ? (
            <div className="flex flex-col gap-4">
              <Field
                label="Email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && email && void sendCode()}
              />
              <Button onClick={() => void sendCode()} disabled={busy || !email.includes('@')}>
                {busy ? 'Sending…' : 'Send my code'}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-ink text-body">
                Check <span className="text-ink font-mono">{cleanEmail()}</span>. Enter the
                six-digit code, or tap the link in the email.
              </p>
              <Field
                label="Six-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                className="text-score text-center font-mono tracking-[0.4em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && void verifyCode()}
              />
              <Button onClick={() => void verifyCode()} disabled={busy || code.length !== 6}>
                {busy ? 'Checking…' : 'Sign in'}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setPhase('email')
                  setCode('')
                  setError(null)
                }}
                className="text-ink-dim hover:text-ink text-body underline underline-offset-4"
              >
                Use a different address
              </button>
            </div>
          )}

          {error && (
            <p role="alert" className="border-flag text-flag text-body mt-5 border-l-2 pl-3">
              {error}
            </p>
          )}
        </div>
      </main>
    </>
  )
}
